// Razorpay Service for handling payment integration
import { RAZORPAY_CONFIG, getRazorpayKey } from '../config/razorpayConfig';

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id?: string;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
  theme?: {
    color?: string;
  };
  handler: (response: any) => void;
  modal?: {
    ondismiss?: () => void;
  };
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

class RazorpayService {
  private static instance: RazorpayService;
  private isScriptLoaded = false;

  private constructor() {}

  static getInstance(): RazorpayService {
    if (!RazorpayService.instance) {
      RazorpayService.instance = new RazorpayService();
    }
    return RazorpayService.instance;
  }

  // Load Razorpay script dynamically
  private loadScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isScriptLoaded && window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        this.isScriptLoaded = true;
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  // Get Razorpay key from configuration
  private getKey(): string {
    return getRazorpayKey();
  }

  // Open Razorpay payment modal
  async openPayment(amount: number, purpose: string = 'Donation'): Promise<void> {
    try {
      // Load Razorpay script if not already loaded
      const isLoaded = await this.loadScript();
      
      if (!isLoaded) {
        throw new Error('Razorpay SDK failed to load');
      }

      // Convert amount to paise (Razorpay expects amount in smallest currency unit)
      const amountInPaise = Math.round(amount * 100);

      const options: RazorpayOptions = {
        key: this.getKey(),
        amount: amountInPaise,
        currency: RAZORPAY_CONFIG.CURRENCY,
        name: RAZORPAY_CONFIG.COMPANY_NAME,
        description: `${purpose} - ${RAZORPAY_CONFIG.COMPANY_DESCRIPTION}`,
        image: '/favicon.ico', // You can add your logo here
        prefill: RAZORPAY_CONFIG.TEST_PREFILL,
        notes: {
          purpose: purpose,
          timestamp: new Date().toISOString(),
        },
        theme: {
          color: RAZORPAY_CONFIG.THEME_COLOR,
        },
        handler: (response: any) => {
          this.handlePaymentSuccess(response, amount, purpose);
        },
        modal: {
          ondismiss: () => {
            this.handlePaymentDismiss(amount, purpose);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Razorpay payment error:', error);
      this.handlePaymentError(error, amount, purpose);
    }
  }

  // Handle successful payment
  private handlePaymentSuccess(response: any, amount: number, purpose: string): void {
    console.log('Payment successful:', {
      paymentId: response.razorpay_payment_id,
      amount,
      purpose,
      timestamp: new Date().toISOString(),
    });

    // Show success message
    alert(`Payment successful! 
Payment ID: ${response.razorpay_payment_id}
Amount: ₹${amount}
Purpose: ${purpose}
Thank you for your donation!`);

    // Here you would typically:
    // 1. Send payment details to your backend
    // 2. Update the UI to show success state
    // 3. Redirect to success page
    // 4. Send confirmation email
  }

  // Handle payment dismissal
  private handlePaymentDismiss(amount: number, purpose: string): void {
    console.log('Payment dismissed:', { amount, purpose });
    // You can add analytics or user feedback here
  }

  // Handle payment errors
  private handlePaymentError(error: any, amount: number, purpose: string): void {
    console.error('Payment error:', error);
    alert(`Payment failed: ${error.message || 'Something went wrong'}
Please try again or contact support.`);
  }

  // Utility method to validate amount
  static validateAmount(amount: number): boolean {
    return typeof amount === 'number' && amount > 0 && Number.isFinite(amount);
  }
}

export default RazorpayService; 